import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { FaArrowRight, FaCircleCheck } from "react-icons/fa6";
import { useAppSelector } from "../../../redux/hooks";
import { css } from "../../../../styled-system/css";
import { Box, Flex, HStack, VStack } from "../../../../styled-system/jsx";
import DismissibleBanner from "../../../components/core/basic/DismissibleBanner";
import { PhaseProgressBar } from "./PhaseProgressBar";
import type { IngestionModel, IngestionPhase } from "../types";

const DATA_STATUS_PATH = "/user/data-status";

export const DashboardBanner = ({ model, spApiMissing }: { model: IngestionModel; spApiMissing?: boolean }) => {
  const { t } = useTranslation(["onboarding"]);
  const orgId = useAppSelector((state) => state.user.organization_id);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const isStaff = useAppSelector((state) => state.user.is_staff);

  const recent = model.phases.find((p) => p.key === "recent") ?? null;
  const backfill = model.phases.find((p) => p.key === "backfill") ?? null;
  const enrichment = model.phases.find((p) => p.key === "enrichment");
  const enrichmentRunning = enrichment?.status === "running";
  const searchQuery = enrichment?.subStreams?.find((sub) => sub.key === "search_query");

  const viewDetails = (
    <Link
      to={DATA_STATUS_PATH}
      className={css({
        display: "inline-flex",
        alignItems: "center",
        gap: "xsmall",
        color: "royalPurple",
        fontSize: "small",
        fontWeight: "bold",
        textDecoration: "none",
        whiteSpace: "nowrap",
      })}
    >
      {t(($) => $.onboarding["View details"])} <FaArrowRight size={12} />
    </Link>
  );

  // Core history in, enrichment still landing: dismissible for a day at a time
  // until the settlement predicate calls it done.
  if (model.isComplete && enrichmentRunning) {
    return (
      <DismissibleBanner
        storageKey={`workflow-progress-v2:enrichment-dismissed:${orgId}:${channel}`}
        dismissForDays={1}
        css={{
          ".container-fluid &": { marginX: "-7.5px" },
          backgroundColor: "royalPurple/8",
          borderBottom: "1px solid {colors.royalPurple/15}",
          color: "text.heading",
          justifyContent: "flex-start",
          textAlign: "left",
          paddingX: "xlarge",
          gap: "medium",
        }}
      >
        <HStack gap="medium" alignItems="center" flex="1" minWidth="0" paddingRight="40px">
          <FaCircleCheck size={20} className={css({ color: "green.600" })} />
          <Box flex="1" minWidth="0">
            <Box fontSize="small" fontWeight="bold">
              {t(($) => $.onboarding["Core data loaded - insights are still importing"])}
            </Box>
            <Box fontSize="small" color="text.secondary">
              {t(
                ($) =>
                  $.onboarding[
                    "Search query performance and traffic data keep loading in the background. Recent weeks may look incomplete until this finishes."
                  ],
              )}
            </Box>
          </Box>
          {searchQuery?.percent != null && (
            <Box fontSize="small" fontWeight="bold" color="royalPurple" whiteSpace="nowrap">
              {searchQuery.percent}%
            </Box>
          )}
          {isStaff && viewDetails}
        </HStack>
      </DismissibleBanner>
    );
  }

  if (model.isComplete) {
    return (
      <DismissibleBanner
        storageKey={`workflow-progress-v2:complete-dismissed:${orgId}:${channel}`}
        css={{
          ".container-fluid &": { marginX: "-7.5px" },
          backgroundColor: "green.600/10",
          borderBottom: "1px solid {colors.green.600/20}",
          color: "text.heading",
          justifyContent: "flex-start",
          textAlign: "left",
          paddingX: "xlarge",
          gap: "medium",
        }}
      >
        <HStack gap="medium" alignItems="center">
          <FaCircleCheck size={20} className={css({ color: "green.600" })} />
          <Box>
            <Box fontSize="small" fontWeight="bold">
              {t(($) => $.onboarding["Your data is fully loaded"])}
            </Box>
            <Box fontSize="small" color="text.secondary">
              {t(($) => $.onboarding["Every report now reflects your complete history."])}
            </Box>
          </Box>
        </HStack>
      </DismissibleBanner>
    );
  }

  const loading: { title: string; sub: string; phase: IngestionPhase | null } =
    !model.isReady ?
      {
        title: t(($) => $.onboarding["Trellis is importing your last 90 days of data"]),
        sub: t(($) => $.onboarding["Enough to start working — the rest loads in the background."]),
        phase: recent,
      }
    : {
        title:
          spApiMissing ?
            t(($) => $.onboarding["Backfilling the rest of your data."])
          : t(($) => $.onboarding["Loading your 2-year sales history"]),
        sub:
          t(($) => $.onboarding["Year-over-year and period comparisons unlock as it finishes."]) +
          (spApiMissing ?
            " " +
            t(($) => $.onboarding["Authorize the Selling Partner API to ingest 2 years of historical sales data."])
          : ""),
        phase: backfill,
      };

  return (
    <Flex
      className={css({ ".container-fluid &": { marginX: "-7.5px" } })}
      alignItems="center"
      gap="medium"
      paddingX="xlarge"
      paddingY="medium"
      backgroundColor="royalPurple/8"
      borderBottom="1px solid {colors.royalPurple/15}"
    >
      <VStack alignItems="flex-start" gap="0" flex="1" minWidth="0">
        <Box fontSize="small" fontWeight="bold" color="text.heading">
          {loading.title}
        </Box>
        <Box fontSize="small" color="text.secondary">
          {loading.phase?.delayed ?
            t(
              ($) =>
                $.onboarding[
                  "This is taking a little longer than usual — no action needed; it'll finish automatically."
                ],
            )
          : loading.sub}
        </Box>
      </VStack>

      {loading.phase?.percent != null && !loading.phase.delayed && (
        <VStack alignItems="stretch" gap="2px" width="180px" flex="none">
          <Box alignSelf="flex-end" fontSize="small" fontWeight="bold" color="royalPurple">
            {loading.phase.percent}%
          </Box>
          <PhaseProgressBar percent={loading.phase.percent} height={6} trackColor="rgba(46, 0, 84, 0.22)" />
        </VStack>
      )}

      {isStaff && viewDetails}
    </Flex>
  );
};
